// lib/fetchState.js
// Telling "the server said no" apart from "the server did not answer".
//
// Pages here used to load with `.catch(() => null)` and then read the null as an
// answer. That is fine for decoration and dangerous for ownership: a blip on
// /api/workbooks made an owner look like a non-owner, which bounced them out of
// a library they had paid for and, on the sales page, put a live buy button in
// front of something they already owned. The failure has to stay visible as a
// failure, so the only thing a page does with it is say so and offer a retry.
//
// Three outcomes, because 404 is an answer and a dead socket is not:
//   ok       the server answered; `data` is that answer, even if it is empty
//   missing  the server answered 404: this thing does not exist
//   failed   no usable answer: network, timeout, 5xx, 401, anything else

export const OK = "ok";
export const MISSING = "missing";
export const FAILED = "failed";
export const LOADING = "loading";

export const LOADING_STATE = { status: LOADING, data: null, error: null };

// Wraps a request so it always resolves, carrying the outcome alongside the
// data instead of collapsing both into one value.
export function settled(promise) {
  return promise.then(
    (data) => ({ status: OK, data, error: null }),
    (error) => ({
      status: error?.status === 404 ? MISSING : FAILED,
      data: null,
      error,
    }),
  );
}

// True only when the server actually answered. Anything that reads as a
// decision about access or ownership must be gated on this.
export const isAnswered = (state) => state?.status === OK;

export const isFailed = (state) => state?.status === FAILED;
export const isMissing = (state) => state?.status === MISSING;
export const isLoading = (state) => state?.status === LOADING;

// True when any of the given states failed, for a page deciding whether to
// render an error panel instead of its content.
export const anyFailed = (...states) => states.some(isFailed);
